// Template spec validation (M1-06; 02 §template, 06 §Templates). New code: Work OS's
// `templates.ts` instantiates one container's tasks and has no keyed cross-references, so only its
// placeholder idea carries over (`{{param}}`).
//
// Two stages, both pure:
//   - `checkSpec(spec)`: the spec on its own, at create and update — keys unique per kind, every
//     reference names an entry of the right kind, no supervisor or team-parent cycle, every
//     `{{placeholder}}` a declared parameter, every schedule a supported RRULE;
//   - `resolveSpec(spec, params)`: at preview and apply — required parameters present, unknown
//     ones refused, defaults filled, placeholders substituted, and names still valid afterwards.
// A problem is `{path, message}` with a JSON-ish path (`seats[2].supervisor`); nothing here
// looks at a company's rows (reuse by name and metric keys are the API's).
import type { TemplateSpec } from "@companyos/schema/contracts";
import { parseRule } from "../recurrence";

export type Problem = { path: string; message: string };

const PLACEHOLDER = /\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g;
/** Text fields' limits after substitution (the schema's `text(LIMITS.name)` before it). */
export const NAME_MAX = 200;

/** Every `{{param}}` named in a string. */
export function placeholdersOf(text: string): string[] {
  return [...text.matchAll(PLACEHOLDER)].map((m) => m[1] as string);
}

/** `text` with each `{{param}}` replaced by its value (unknown ones are left as they are). */
export function substitute(text: string, params: Readonly<Record<string, string>>): string {
  return text.replace(PLACEHOLDER, (whole, key: string) => params[key] ?? whole);
}

/** Every text field of a spec that may carry placeholders, with its path. */
function textFields(spec: TemplateSpec): { path: string; value: string }[] {
  const out: { path: string; value: string }[] = [];
  const add = (path: string, value: string | undefined) => {
    if (value !== undefined) out.push({ path, value });
  };
  spec.roles?.forEach((r, i) => {
    add(`roles[${i}].name`, r.name);
    add(`roles[${i}].description`, r.description);
  });
  spec.teams?.forEach((t, i) => {
    add(`teams[${i}].name`, t.name);
  });
  spec.seats?.forEach((s, i) => {
    add(`seats[${i}].title`, s.title);
  });
  spec.responsibilities?.forEach((r, i) => {
    add(`responsibilities[${i}].name`, r.name);
    add(`responsibilities[${i}].description`, r.description);
    r.recurring?.forEach((x, j) => {
      add(`responsibilities[${i}].recurring[${j}].task.title`, x.task.title);
      add(`responsibilities[${i}].recurring[${j}].task.description`, x.task.description);
    });
  });
  spec.spaces?.forEach((s, i) => {
    add(`spaces[${i}].name`, s.name);
    s.channels?.forEach((c, j) => {
      add(`spaces[${i}].channels[${j}].name`, c.name);
    });
    s.boards?.forEach((b, j) => {
      add(`spaces[${i}].boards[${j}].name`, b.name);
      b.fields?.forEach((f, k) => {
        add(`spaces[${i}].boards[${j}].fields[${k}].name`, f.name);
      });
      b.views?.forEach((v, k) => {
        add(`spaces[${i}].boards[${j}].views[${k}].name`, v.name);
      });
      b.tasks?.forEach((t, k) => {
        add(`spaces[${i}].boards[${j}].tasks[${k}].title`, t.title);
      });
    });
  });
  return out;
}

/** The first cycle through `next` (key → key), as the keys on it, or null. */
function cycleOf(
  keys: readonly string[],
  next: (k: string) => string | undefined,
): string[] | null {
  for (const start of keys) {
    const seen: string[] = [];
    let cur: string | undefined = start;
    while (cur !== undefined) {
      const at = seen.indexOf(cur);
      if (at >= 0) return seen.slice(at);
      seen.push(cur);
      cur = next(cur);
    }
  }
  return null;
}

/** Structural problems of a spec (create and update refuse a spec with any). */
export function checkSpec(spec: TemplateSpec): Problem[] {
  const problems: Problem[] = [];
  const unique = (kind: string, keys: readonly (string | undefined)[]) => {
    const seen = new Set<string>();
    keys.forEach((k, i) => {
      if (k === undefined) return;
      if (seen.has(k))
        problems.push({ path: `${kind}[${i}].key`, message: `duplicate key "${k}"` });
      seen.add(k);
    });
    return seen;
  };
  const params = unique("params", spec.params?.map((p) => p.key) ?? []);
  const roles = unique("roles", spec.roles?.map((r) => r.key) ?? []);
  const teams = unique("teams", spec.teams?.map((t) => t.key) ?? []);
  const seats = unique("seats", spec.seats?.map((s) => s.key) ?? []);
  unique("spaces", spec.spaces?.map((s) => s.key) ?? []);
  const boardKeys = (spec.spaces ?? []).flatMap((s) => (s.boards ?? []).map((b) => b.key));
  const boards = new Set<string>();
  for (const b of boardKeys) {
    if (boards.has(b))
      problems.push({ path: "spaces[].boards", message: `duplicate board key "${b}"` });
    boards.add(b);
  }
  const ref = (path: string, key: string | undefined, set: Set<string>, kind: string) => {
    if (key !== undefined && !set.has(key))
      problems.push({ path, message: `no ${kind} with key "${key}" in the template` });
  };
  spec.params?.forEach((p, i) => {
    if (p.required && p.default !== undefined)
      problems.push({
        path: `params[${i}].default`,
        message: "a required parameter has no default",
      });
  });
  spec.teams?.forEach((t, i) => {
    ref(`teams[${i}].parent`, t.parent, teams, "team");
    ref(`teams[${i}].lead_seat`, t.lead_seat, seats, "seat");
    if (t.parent === t.key)
      problems.push({ path: `teams[${i}].parent`, message: "a team cannot be its own parent" });
  });
  spec.seats?.forEach((s, i) => {
    ref(`seats[${i}].role`, s.role, roles, "role");
    ref(`seats[${i}].supervisor`, s.supervisor, seats, "seat");
    ref(`seats[${i}].team`, s.team, teams, "team");
  });
  spec.responsibilities?.forEach((r, i) => {
    ref(`responsibilities[${i}].seat`, r.seat, seats, "seat");
    r.recurring?.forEach((x, j) => {
      const path = `responsibilities[${i}].recurring[${j}]`;
      ref(`${path}.board`, x.board, boards, "board");
      const rule = parseRule(x.schedule);
      if (!rule.ok) problems.push({ path: `${path}.schedule`, message: rule.reason });
    });
  });
  spec.spaces?.forEach((s, i) => {
    ref(`spaces[${i}].team`, s.team, teams, "team");
    // W1-FIXES: a board's fields have distinct names; a view's date is a date it has
    s.boards?.forEach((b, j) => {
      const path = `spaces[${i}].boards[${j}]`;
      const names = new Set<string>();
      b.fields?.forEach((f, k) => {
        const n = f.name.toLowerCase();
        if (names.has(n))
          problems.push({
            path: `${path}.fields[${k}].name`,
            message: `duplicate field "${f.name}"`,
          });
        names.add(n);
      });
      const dates = new Set(
        (b.fields ?? []).filter((f) => f.type === "date").map((f) => f.name.toLowerCase()),
      );
      b.views?.forEach((v, k) => {
        const d = v.date_field;
        if (d !== undefined && d !== "due_at" && d !== "start_at" && !dates.has(d.toLowerCase()))
          problems.push({
            path: `${path}.views[${k}].date_field`,
            message: `"${d}" is not due_at, start_at or a date field of the board`,
          });
      });
    });
  });
  const supervisor = new Map((spec.seats ?? []).map((s) => [s.key, s.supervisor]));
  const seatCycle = cycleOf([...supervisor.keys()], (k) => supervisor.get(k));
  if (seatCycle)
    problems.push({ path: "seats", message: `supervisor cycle: ${seatCycle.join(" → ")}` });
  const parent = new Map((spec.teams ?? []).map((t) => [t.key, t.parent]));
  const teamCycle = cycleOf([...parent.keys()], (k) => parent.get(k));
  if (teamCycle)
    problems.push({ path: "teams", message: `team parent cycle: ${teamCycle.join(" → ")}` });
  for (const f of textFields(spec))
    for (const key of placeholdersOf(f.value))
      if (!params.has(key))
        problems.push({ path: f.path, message: `{{${key}}} is not a declared parameter` });
  return problems;
}

export type Resolved = { spec: TemplateSpec; params: Record<string, string>; problems: Problem[] };

/**
 * The spec with `given` parameters applied: defaults filled, required ones checked, unknown ones
 * refused, placeholders substituted. Structural problems (`checkSpec`) are included.
 */
export function resolveSpec(spec: TemplateSpec, given: Readonly<Record<string, string>>): Resolved {
  const problems = checkSpec(spec);
  const declared = new Map((spec.params ?? []).map((p) => [p.key, p]));
  const params: Record<string, string> = {};
  for (const key of Object.keys(given))
    if (!declared.has(key))
      problems.push({ path: `params.${key}`, message: "not a parameter of this template" });
  for (const [key, p] of declared) {
    const value = (given[key] ?? "").trim() || p.default;
    if (value === undefined || value === "") {
      if (p.required) problems.push({ path: `params.${key}`, message: `${p.label} is required` });
      else params[key] = "";
      continue;
    }
    params[key] = value;
  }
  const sub = (text: string) => substitute(text, params).replace(/\s+/g, " ").trim();
  const subOpt = (text: string | undefined) => (text === undefined ? undefined : sub(text));
  const out: TemplateSpec = structuredClone(spec);
  out.roles?.forEach((r) => {
    r.name = sub(r.name);
    if (r.description !== undefined) r.description = subOpt(r.description) as string;
  });
  out.teams?.forEach((t) => {
    t.name = sub(t.name);
  });
  out.seats?.forEach((s) => {
    s.title = sub(s.title);
  });
  out.responsibilities?.forEach((r) => {
    r.name = sub(r.name);
    if (r.description !== undefined) r.description = subOpt(r.description) as string;
    r.recurring?.forEach((x) => {
      x.task.title = sub(x.task.title);
      if (x.task.description !== undefined)
        x.task.description = subOpt(x.task.description) as string;
    });
  });
  out.spaces?.forEach((s) => {
    s.name = sub(s.name);
    s.channels?.forEach((c) => {
      c.name = sub(c.name);
    });
    s.boards?.forEach((b) => {
      b.name = sub(b.name);
      b.fields?.forEach((f) => {
        f.name = sub(f.name);
      });
      b.views?.forEach((v) => {
        v.name = sub(v.name);
        // the date field is named as the fields are: substituted the same way (Codex W1 r1)
        if (v.date_field !== undefined) v.date_field = sub(v.date_field);
      });
      b.tasks?.forEach((t) => {
        t.title = sub(t.title);
      });
    });
  });
  // Names must still be names once the parameters are in (an empty parameter can empty one).
  for (const f of textFields(out))
    if (/(\.name|\.title)$/.test(f.path) && !/description/.test(f.path)) {
      if (f.value.length === 0)
        problems.push({ path: f.path, message: "is empty after the parameters" });
      if (f.value.length > NAME_MAX)
        problems.push({
          path: f.path,
          message: `is longer than ${NAME_MAX} characters after the parameters`,
        });
    }
  return { spec: out, params, problems };
}
